import React from 'react';
import { Link } from 'react-router-dom';
import { Clock, ChevronRight, ArrowRight, Book, Phone, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { getAllMoldGuides } from '../data/moldGuidesData';
import { businessConfig } from '../data/businessConfig';
import SEOHead from '../components/SEOHead';

const GuidesListPage = () => {
  const guides = getAllMoldGuides();
  
  // Group by category
  const categories = [...new Set(guides.map(g => g.category))];

  return (
    <div>
      <SEOHead 
        title="Mold Remediation Guides | Dallas Homeowner Resources | Gray Contracting"
        description="Expert guides on mold remediation in Texas. Learn about the process, costs, insurance, and what to expect when dealing with mold in your Dallas-Fort Worth home."
        canonical="/guides"
      />

      {/* Hero */}
      <section className="py-16 bg-neutral-900">
        <div className="max-w-7xl mx-auto px-4">
          <nav className="flex items-center gap-2 text-sm text-neutral-400 mb-6">
            <Link to="/" className="hover:text-amber-400">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-amber-400">Guides</span>
          </nav>
          
          <div className="flex items-center gap-3 mb-4">
            <Book className="w-8 h-8 text-amber-500" />
            <span className="text-amber-400 font-medium">Homeowner Resources</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Mold Remediation Guides
          </h1>
          <p className="text-xl text-neutral-300 max-w-3xl">
            Expert information to help Dallas-Fort Worth homeowners understand mold remediation, 
            Texas regulations, costs, and the process from start to finish.
          </p>
        </div>
      </section>

      {/* Guides Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          {categories.map(category => (
            <div key={category} className="mb-12">
              <h2 className="text-2xl font-bold text-neutral-900 mb-6 flex items-center gap-2">
                <FileText className="w-6 h-6 text-amber-500" />
                {category}
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {guides.filter(g => g.category === category).map(guide => (
                  <Link 
                    key={guide.slug}
                    to={`/guides/${guide.slug}`}
                    className="group bg-white border border-neutral-200 rounded-xl overflow-hidden hover:shadow-xl hover:border-amber-300 transition-all"
                  >
                    <div className="aspect-video overflow-hidden">
                      <img 
                        src={guide.heroImage} 
                        alt={guide.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-2 text-sm text-neutral-500 mb-2">
                        <Clock className="w-4 h-4" />
                        <span>{guide.readTime}</span>
                      </div>
                      <h3 className="text-lg font-semibold text-neutral-900 group-hover:text-amber-600 transition-colors mb-2">
                        {guide.title}
                      </h3>
                      <p className="text-sm text-neutral-600 line-clamp-2">
                        {guide.metaDescription}
                      </p>
                      <div className="mt-4 flex items-center text-amber-600 font-medium text-sm">
                        Read Guide <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-amber-500">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Have a Mold Question?
          </h2>
          <p className="text-white/90 mb-8">
            Our team is ready to help you understand your mold situation and discuss next steps.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${businessConfig.phoneTel}`}>
              <Button size="lg" className="bg-neutral-900 hover:bg-neutral-800 text-white px-8">
                <Phone className="w-5 h-5 mr-2" />
                Call {businessConfig.phone}
              </Button>
            </a>
            <Link to="/services/mold-remediation">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-amber-600 px-8">
                View Mold Services
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GuidesListPage;
